import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ICurrencyExchangeRepository } from '@app/domain/ports';
import type { CurrencyExchange, CurrencyExchangeListProjection } from '@app/domain/entities';
import type { CurrencyExchangeFilter, CurrencyExchangeCursor } from '@app/domain/ports';

interface CurrencyExchangeRow extends Omit<CurrencyExchange, 'date'> {
  date: number;
}

export class CurrencyExchangeRepository extends BaseSqliteRepository implements ICurrencyExchangeRepository {
  private readonly BASE_SELECT = `
    SELECT
      e._id as id,
      e.exchange_date as date,
      e.account_id as accountId,
      a.name as accountName,
      e.amount_out as fromAmount,
      cl1.ticker as fromCurrencyCode,
      e.amount_in as toAmount,
      cl2.ticker as toCurrencyCode,
      e.note as note
  `;

  private readonly BASE_FROM = `
    FROM exchanges e
    INNER JOIN accounts a ON e.account_id = a._id
    INNER JOIN currencies c1 ON e.currency_out_id = c1._id
    INNER JOIN available_currencies cl1 ON c1.available_currency_id = cl1._id
    INNER JOIN currencies c2 ON e.currency_in_id = c2._id
    INNER JOIN available_currencies cl2 ON c2.available_currency_id = cl2._id
  `;

  private readonly BASE_WHERE = `WHERE e.is_deleted = 0`;

  async findAll(
    cursor: CurrencyExchangeCursor,
    filter: CurrencyExchangeFilter,
  ): Promise<CurrencyExchangeListProjection[]> {
    const sql = `
      ${this.BASE_SELECT},
        CASE WHEN LAG(e.exchange_date) OVER (ORDER BY e.exchange_date DESC, e._id DESC) IS NULL OR LAG(e.exchange_date) OVER (ORDER BY e.exchange_date DESC, e._id DESC) != e.exchange_date THEN 1 ELSE 0 END as isFirstInDate
      ${this.BASE_FROM}
      ${this.BASE_WHERE}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY e.exchange_date DESC, e._id DESC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<CurrencyExchangeListProjection & { isFirstInDate: number }>(query, params);
    return rows.map((row) =>
      this.mapRowToEntity<CurrencyExchangeRow & { isFirstInDate: number }, CurrencyExchangeListProjection>(
        row,
        (r, m) => (m.isFirstInDate = r.isFirstInDate === 1),
      ),
    );
  }

  async count(filter: CurrencyExchangeFilter): Promise<number> {
    const sql = `
      SELECT count(*) as count
      ${this.BASE_FROM}
      ${this.BASE_WHERE}
    `;
    const { query, params } = this.applyFilter(sql, filter);

    const row = await this.queryOne<{ count: number }>(query, params);
    return row?.count ?? 0;
  }

  async getById(id: number): Promise<CurrencyExchange | null> {
    const sql = `
      ${this.BASE_SELECT}
      ${this.BASE_FROM}
      ${this.BASE_WHERE}
        AND e._id = ?
    `;

    const row = await this.queryOne<CurrencyExchangeRow>(sql, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(data: Omit<CurrencyExchange, 'id' | 'accountName'>): Promise<number> {
    const sql = `
      INSERT INTO exchanges (exchange_date, account_id, currency_out_id, amount_out, currency_in_id, amount_in, note, is_deleted)
      VALUES (
        ?, ?,
        (SELECT c._id as id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
        ?,
        (SELECT c._id as id  FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
        ?, ?, 0)
      RETURNING _id AS id
    `;

    const result = await this.queryOne<{ id: number }>(sql, [
      new Date(data.date).setUTCHours(0, 0, 0, 0) / 1000,
      data.accountId,
      data.fromCurrencyCode,
      data.fromAmount,
      data.toCurrencyCode,
      data.toAmount,
      data.note,
    ]);

    if (!result?.id) throw new SqliteQueryError('INSERT INTO currency_exchanges', [], 'Failed to insert exchange');

    return result.id;
  }

  async update(data: Omit<CurrencyExchange, 'accountName'>): Promise<void> {
    const sql = `
      UPDATE exchanges
      SET exchange_date = ?,
          account_id = ?,
          amount_out = ?,
          currency_out_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
          amount_in = ?,
          currency_in_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
          note = ?
      WHERE _id = ? AND is_deleted = 0
    `;

    await this.execute(sql, [
      new Date(data.date).setUTCHours(0, 0, 0, 0) / 1000,
      data.accountId,
      data.fromAmount,
      data.fromCurrencyCode,
      data.toAmount,
      data.toCurrencyCode,
      data.note,
      data.id,
    ]);
  }

  async delete(id: number): Promise<void> {
    const sql = `
      UPDATE exchanges
      SET is_deleted = (SELECT max(is_deleted) + 1 FROM exchanges),
          updated_at = strftime('%s','now')
      WHERE _id = ?
    `;
    await this.execute(sql, [id]);
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM exchanges WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE exchanges
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  async findSimilarExchange(params: {
    date: Date;
    accountId: number;
    fromAmount: number;
    fromCurrencyCode: string;
    toAmount: number;
    toCurrencyCode: string;
    amountTolerance: number;
    externalSystem: string;
  }): Promise<number | null> {
    const dateSeconds = new Date(params.date).setUTCHours(0, 0, 0, 0) / 1000;

    const sql = `
      SELECT e._id as id
      FROM exchanges e
      INNER JOIN currencies c1 ON e.currency_out_id = c1._id
      INNER JOIN available_currencies cl1 ON c1.available_currency_id = cl1._id
      INNER JOIN currencies c2 ON e.currency_in_id = c2._id
      INNER JOIN available_currencies cl2 ON c2.available_currency_id = cl2._id
      WHERE e.is_deleted = 0
        AND e.exchange_date = ?
        AND e.account_id = ?
        AND cl1.ticker = ?
        AND cl2.ticker = ?
        AND e.amount_out BETWEEN ? AND ?
        AND e.amount_in BETWEEN ? AND ?
        AND NOT EXISTS (
          SELECT 1 FROM external_transactions ext
          WHERE ext.exchange_id = e._id
            AND ext.source = ?
            AND ext.is_deleted = 0
        )
      LIMIT 1
    `;

    const row = await this.queryOne<{ id: number }>(sql, [
      dateSeconds,
      params.accountId,
      params.fromCurrencyCode,
      params.toCurrencyCode,
      params.fromAmount - params.amountTolerance,
      params.fromAmount + params.amountTolerance,
      params.toAmount - params.amountTolerance,
      params.toAmount + params.amountTolerance,
      params.externalSystem,
    ]);

    return row?.id ?? null;
  }

  private applyFilter(
    query: string,
    filter?: CurrencyExchangeFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.dateFrom) {
      query += ` AND e.exchange_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND e.exchange_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.accountId) {
      query += ` AND e.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.fromCurrencyCode) {
      query += ` AND cl1.ticker = ?`;
      params.push(filter.fromCurrencyCode);
    }

    if (filter?.toCurrencyCode) {
      query += ` AND cl2.ticker = ?`;
      params.push(filter.toCurrencyCode);
    }

    if (filter?.showHiddenCurrencies !== true) {
      query += ` AND c1.is_hidden = 0 AND c2.is_hidden = 0`;
    }

    return { query, params };
  }

  private mapRowToEntity<T extends CurrencyExchangeRow, P extends CurrencyExchange>(
    row: T,
    configure?: (row: T, model: P) => void,
  ): P {
    const model = {
      id: row.id,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      fromAmount: row.fromAmount,
      fromCurrencyCode: row.fromCurrencyCode,
      toAmount: row.toAmount,
      toCurrencyCode: row.toCurrencyCode,
      note: row.note,
    } as P;

    configure?.(row, model);
    return model;
  }
}
